import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Trophy, Medal, Award, FileText } from "lucide-react"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"
import { useTournamentStanding } from "@/graphql/hooks/useTournamentStanding"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { UserAvatar } from "@/components/ui/user-avatar"
import { Button } from "@/components/ui/button"
import { useAuthContext } from "@/contexts/AuthContext"
import { RacesMap } from "@/lib/constants"
import { useTournamentInfo, useTournamentRoundSimple } from "@/graphql"
import { PodioPlayers } from "@/components/PodioPlayers"
import { TournamentCasualInfo } from "./Tournament/Detail/TournamentCasualInfo"

const getRaceName = (id?: number) => RacesMap.get(id!) || ""

const TournamentRoundStanding = () => {
  const { roundId, pdf, tournamentId } = useParams<{
    roundId: string
    tournamentId: string
    pdf?: string
  }>()

  const { isAdmin } = useAuthContext()
  const { tournament, loading: loadingTournament } = useTournamentInfo(Number(tournamentId))
  const { round, loading: loadingRound } = useTournamentRoundSimple(Number(roundId))
  const { standing, loading, error } = useTournamentStanding({
    roundId: Number(roundId),
    drop: true,
  })

  const [activeTab, setActiveTab] = useState<string>("general")

  if (loading || loadingTournament || loadingRound)
    return (
      <Layout>
        <LoadingScreen />
      </Layout>
    )
  if (error || !tournament || !round)
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">Error al cargar las clasificaciones</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )

  // Torneo terminado (5) o reportado (6): se ocultan los códigos de jugador
  // salvo para administradores.
  const isTournamentClosed = [5, 6].includes(tournament.statusId)
  const canSeeCode = isAdmin || !isTournamentClosed

  // Razas únicas presentes en el standing
  const racesInStanding = Array.from(
    new Set(standing.map((p) => p.TournamentPlayer.raceId)),
  ).filter(Boolean)

  // Filtrar según tab activa
  const filteredStanding =
    activeTab === "general"
      ? standing
      : standing.filter((p) => p.TournamentPlayer.raceId?.toString() === activeTab)

  const podiumPlayers = filteredStanding.slice(0, 5)

  // If this is a PDF route, apply print styles
  const isPrintMode = pdf === "pdf"

  if (isPrintMode) {
    return (
      <div className="print:block print:p-4">
        <div className="p-2">
          <h1 className="text-xl font-bold mb-1 print:mb-1">Posiciones - {round?.name}</h1>
          <h2 className="text-base mb-3 print:mb-2">{tournament?.name}</h2>

          <Table className="w-full text-sm border border-gray-400 dark:border-gray-600 print:border-black">
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-gray-700 print:bg-gray-200">
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">Pos</TableHead>
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">
                  Jugador
                </TableHead>
                {canSeeCode && (
                  <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    Código
                  </TableHead>
                )}
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">Pts</TableHead>
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">OMW</TableHead>
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">PGW</TableHead>
                <TableHead className="px-2 py-1 text-gray-900 dark:text-gray-100">OGW</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {standing.map((player, key) => (
                <TableRow
                  key={key}
                  className="border-t border-gray-300 dark:border-gray-600 print:border-black"
                >
                  <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    {key + 1}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    {player.TournamentPlayer.Person.fullName}
                  </TableCell>
                  {canSeeCode && (
                    <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                      {player.TournamentPlayer.Person.code}
                    </TableCell>
                  )}
                  <TableCell className="px-2 py-1 font-bold text-gray-900 dark:text-gray-100">
                    {player.points}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    {player.omw}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    {player.pgw}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-gray-900 dark:text-gray-100">
                    {player.ogw}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <Layout className="px-0 py-0">
      <HeaderTournament tournament={tournament} round={round} />
      <div className="container mx-auto px-0 py-0">
        <TournamentNavigation
          tournament={tournament}
          tournamentName={tournament.name}
          tournamentStatusId={tournament.statusId}
          currentPage="rounds"
          showRounds
        />

        <div className="mb-6">
          {/* Título */}
          <h1 className="text-3xl font-bold mb-4 flex items-center gap-2">
            <Medal className="h-8 w-8 text-yellow-500" />
            Posiciones {round?.name}
          </h1>

          {/* Tabs */}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              {/* Tab general */}
              <button
                className={`px-2 py-1 rounded-md text-sm font-medium whitespace-nowrap ${
                  activeTab === "general" ? "bg-primary text-white" : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => setActiveTab("general")}
              >
                General
              </button>

              {/* Tabs por raza */}
              {racesInStanding.map((raceId) => (
                <button
                  key={raceId as number}
                  className={`px-2 py-1 rounded-md text-sm font-medium ${activeTab === raceId.toString() ? "bg-primary text-white" : "bg-gray-200 text-gray-700"} whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis`}
                  onClick={() => setActiveTab(raceId.toString())}
                  title={getRaceName(raceId as number)} // tooltip si es muy largo
                >
                  {getRaceName(raceId as number)}
                </button>
              ))}
            </div>
          )}
        </div>

        <PodioPlayers players={podiumPlayers} isAdmin={isAdmin} />

        {/* Tabla completa */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl lg:text-3xl leading-tight break-words whitespace-normal">
                <Trophy className="h-5 w-5" /> Clasificación completa{" "}
                {getRaceName(Number(activeTab))}
              </CardTitle>
              <div className="flex sm:justify-end">
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/tournament/standing/${roundId}/slug/pdf/${tournament.id}`}
                    target="_blank"
                    className="flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Ver Lista para Imprimir
                  </Link>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredStanding.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No hay jugadores para esta categoría.
              </p>
            ) : (
              <>
                {/* Mobile: Cards */}
                <div className="space-y-3 md:hidden">
                  {filteredStanding.map((player, key) => (
                    <Card key={player.id} className="p-3 border">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2 font-bold">
                          {key + 1 === 1 && <Trophy className="h-4 w-4 text-yellow-600" />}
                          {key + 1 === 2 && <Medal className="h-4 w-4 text-slate-600" />}
                          {key + 1 === 3 && <Award className="h-4 w-4 text-amber-600" />}
                          {key + 1}
                        </div>
                        {player.TournamentPlayer.drop === 1 && (
                          <Badge variant={"destructive"}> Drop </Badge>
                        )}
                      </div>
                      <div className="flex justify-center item-center -mt-12 mb-5">
                        <UserAvatar
                          src={player.TournamentPlayer.Person.urlAvatar}
                          name={player.TournamentPlayer.Person.fullName}
                          size="lg"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{player.TournamentPlayer.Person.fullName}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground mt-2">
                        {canSeeCode && <div>Código: {player.TournamentPlayer.Person.code}</div>}
                        {isAdmin && <div>Raza: {getRaceName(player.TournamentPlayer.raceId)}</div>}
                        <div>
                          Puntos: <span className="font-bold">{player.points}</span>
                        </div>
                        <div>OMW: {player.omw}</div>
                        <div>PGW: {player.pgw}</div>
                        <div>OGW: {player.ogw}</div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Desktop: Table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <TableHeader>
                      <TableRow className="bg-gray-100 dark:bg-gray-800">
                        <TableHead className="text-gray-700 dark:text-gray-300">Pos</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Jugador</TableHead>
                        {isAdmin && (
                          <TableHead className="text-gray-700 dark:text-gray-300">Raza</TableHead>
                        )}
                        {canSeeCode && (
                          <TableHead className="text-gray-700 dark:text-gray-300">Código</TableHead>
                        )}
                        <TableHead className="text-center text-gray-700 dark:text-gray-300">
                          Puntos
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">OMW</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">PGW</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">OGW</TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">Estado</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {filteredStanding.map((player, key) => (
                        <TableRow key={key} className={`even:bg-gray-50 dark:even:bg-gray-800`}>
                          <TableCell className="font-bold flex items-center gap-2 text-gray-900 dark:text-gray-100">
                            {activeTab === "general" ? key + 1 : player.position}
                            {key + 1 === 1 && <Trophy className="h-4 w-4 text-yellow-600" />}
                            {key + 1 === 2 && <Medal className="h-4 w-4 text-slate-600" />}
                            {key + 1 === 3 && <Award className="h-4 w-4 text-amber-600" />}
                          </TableCell>

                          <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                            <div className="flex items-center gap-2">
                              <UserAvatar
                                src={player.TournamentPlayer.Person.urlAvatar}
                                name={player.TournamentPlayer.Person.fullName}
                                size="sm"
                              />
                              <div>
                                <div className="font-medium">
                                  {player.TournamentPlayer.Person.fullName}
                                </div>
                                {canSeeCode && (
                                  <div className="text-sm text-muted-foreground md:hidden">
                                    {player.TournamentPlayer.Person.code}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>

                          {isAdmin && (
                            <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                              {getRaceName(player.TournamentPlayer.raceId)}
                            </TableCell>
                          )}
                          {canSeeCode && (
                            <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                              {player.TournamentPlayer.Person.code}
                            </TableCell>
                          )}

                          <TableCell className="font-bold text-center p-2 text-gray-900 dark:text-gray-100">
                            {player.points}
                          </TableCell>
                          <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                            {player.omw}
                          </TableCell>
                          <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                            {player.pgw}
                          </TableCell>
                          <TableCell className="p-2 text-gray-900 dark:text-gray-100">
                            {player.ogw}
                          </TableCell>
                          <TableCell className="p-2">
                            <Badge
                              variant={player.TournamentPlayer.drop ? "destructive" : "secondary"}
                              className={
                                player.TournamentPlayer.drop
                                  ? "bg-red-100 text-red-800 dark:bg-red-700 dark:text-gray-100"
                                  : "bg-green-100 text-green-800 dark:bg-green-700 dark:text-gray-100"
                              }
                            >
                              {player.TournamentPlayer.drop ? "Drop" : "Activo"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <TournamentCasualInfo tournament={tournament} className="mt-2" />
      </div>
    </Layout>
  )
}

export default TournamentRoundStanding
